import { useEffect, useState } from 'react';
import { listPlans, createPlan, updatePlan, deletePlan } from '../../../services/planService';

const DEFAULT_PLANS = [
  { name: 'Basic', price: 3000, shipmentLimit: 20, features: ['20 shipments/month', 'Email support'] },
  { name: 'Pro', price: 7000, shipmentLimit: 60, features: ['60 shipments/month', 'Priority support', 'Bulk upload'] },
  { name: 'Premium', price: 15000, shipmentLimit: 150, features: ['150 shipments/month', 'Priority support', 'Analytics'] },
];

export default function ManagePlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', price: '', shipmentLimit: '', features: '' });
  const [editingId, setEditingId] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const data = await listPlans();
      setPlans(data);
      setError('');
    } catch (e) {
      // Prefill with defaults when server plans cannot be loaded
      setPlans(DEFAULT_PLANS.map(p => ({ ...p, _id: null })));
      // Do not surface an error; allow admin to edit/create from defaults
      setError('');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = {
        name: form.name.trim(),
        price: Number(form.price),
        shipmentLimit: Number(form.shipmentLimit),
        features: form.features ? form.features.split(',').map(f => f.trim()).filter(Boolean) : [],
      };
      if (editingId) {
        await updatePlan(editingId, payload);
      } else {
        await createPlan(payload);
      }
      setForm({ name: '', price: '', shipmentLimit: '', features: '' });
      setEditingId(null);
      await refresh();
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  }

  function edit(plan) {
    setEditingId(plan._id);
    setForm({ name: plan.name, price: String(plan.price), shipmentLimit: String(plan.shipmentLimit), features: (plan.features || []).join(', ') });
  }

  async function remove(plan) {
    // Default (prefilled) plans have no _id; allow removing from view
    if (!plan._id) {
      if (!confirm('Remove this default plan from view?')) return;
      setPlans(prev => prev.filter(p => (p._id ? p._id !== plan._id : p.name !== plan.name)));
      return;
    }
    if (!confirm('Delete this plan?')) return;
    try { await deletePlan(plan._id); await refresh(); } catch (e) { setError('Delete failed'); }
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">Manage Plans</h2>
      {error && <div className="mb-4 text-red-600">{error}</div>}

      <form onSubmit={submit} className="bg-white p-4 rounded shadow mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm mb-1">Name</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border rounded p-2" required />
        </div>
        <div>
          <label className="block text-sm mb-1">Price (NGN)</label>
          <input type="number" min="0" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="w-full border rounded p-2" required />
        </div>
        <div>
          <label className="block text-sm mb-1">Shipment Limit (per month)</label>
          <input type="number" min="0" value={form.shipmentLimit} onChange={e => setForm({ ...form, shipmentLimit: e.target.value })} className="w-full border rounded p-2" required />
        </div>
        <div>
          <label className="block text-sm mb-1">Features (comma-separated)</label>
          <input value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} className="w-full border rounded p-2" />
        </div>
        <div className="md:col-span-2">
          <button type="submit" className="bg-blue-600 text-white py-2 px-4 rounded">
            {editingId ? 'Update Plan' : 'Add Plan'}
          </button>
          {editingId && (
            <button type="button" className="ml-2 py-2 px-4 rounded border" onClick={() => { setEditingId(null); setForm({ name: '', price: '', shipmentLimit: '', features: '' }); }}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="bg-white rounded shadow">
        <table className="min-w-full divide-y">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Price</th>
              <th className="px-4 py-2 text-left">Limit</th>
              <th className="px-4 py-2 text-left">Features</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-4 py-3" colSpan="5">Loading…</td></tr>
            ) : plans.length === 0 ? (
              <tr><td className="px-4 py-3" colSpan="5">No plans found</td></tr>
            ) : plans.map(p => (
              <tr key={p._id || p.name} className="border-t">
                <td className="px-4 py-2">{p.name}</td>
                <td className="px-4 py-2">NGN {Number(p.price).toLocaleString()}</td>
                <td className="px-4 py-2">{p.shipmentLimit}</td>
                <td className="px-4 py-2 text-sm">{(p.features || []).join(', ')}</td>
                <td className="px-4 py-2 text-right">
                  <button className="mr-2 text-blue-600" onClick={() => edit(p)}>Edit</button>
                  <button className="text-red-600" onClick={() => remove(p)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}